<template>
  <div class="registration-container">
    <!-- Progress Header (like student registration) -->
    <div class="progress-header">
      <div class="header-content">
        <div class="title-section">
          <div class="back-button" @click="goBack">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z" />
            </svg>
          </div>
          <div class="title-content">
            <h1 class="main-title">Tutor Registration</h1>
            <p class="subtitle">Join our community of dedicated educators and make a difference!</p>
          </div>
        </div>

        <!-- Step Progress (2 steps) -->
        <div class="step-progress">
          <div class="step completed">
            <span class="step-number">1</span>
          </div>
          <div class="step-line"></div>
          <div class="step active">
            <span class="step-number">2</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <!-- Document Upload and Terms Section -->
      <div class="form-card">
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                />
              </svg>
            </div>
            <div class="section-title">
              <h3>Document Upload</h3>
              <p>Upload all required documents for verification</p>
            </div>
          </div>

          <!-- Document Grid -->
          <div class="documents-grid">
            <!-- Official Transcript -->
            <div class="document-item">
              <div class="document-header">
                <h4>Official Transcript of Grades <span class="required">*</span></h4>
                <p>Official academic transcript from your institution</p>
              </div>
              <div
                class="upload-area"
                @click="triggerFileInput('transcript')"
                @drop="handleDrop($event, 'transcript')"
                @dragover.prevent
                @dragenter.prevent
              >
                <input
                  ref="transcriptInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  @change="handleFileChange($event, 'transcript')"
                  style="display: none"
                />
                <div v-if="!documents.transcript" class="upload-placeholder">
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                  </svg>
                  <p>Click to upload or drag and drop</p>
                  <span>PDF, JPG, PNG, DOC, DOCX (Max 10MB)</span>
                </div>
                <div v-else class="uploaded-file">
                  <svg class="file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                    />
                  </svg>
                  <div class="file-info">
                    <span class="file-name">{{ documents.transcript.name }}</span>
                    <span class="file-size">{{ formatFileSize(documents.transcript.size) }}</span>
                  </div>
                  <button @click.stop="removeFile('transcript')" class="remove-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.transcript" class="error-message">{{ errors.transcript }}</span>
            </div>

            <!-- Certificate of Grade -->
            <div class="document-item">
              <div class="document-header">
                <h4>Certificate of Grade</h4>
                <p>Optional: Additional grade certificates</p>
              </div>
              <div
                class="upload-area"
                @click="triggerFileInput('certificate')"
                @drop="handleDrop($event, 'certificate')"
                @dragover.prevent
                @dragenter.prevent
              >
                <input
                  ref="certificateInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  @change="handleFileChange($event, 'certificate')"
                  style="display: none"
                />
                <div v-if="!documents.certificate" class="upload-placeholder">
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                  </svg>
                  <p>Click to upload or drag and drop</p>
                  <span>PDF, JPG, PNG, DOC, DOCX (Max 10MB)</span>
                </div>
                <div v-else class="uploaded-file">
                  <svg class="file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                    />
                  </svg>
                  <div class="file-info">
                    <span class="file-name">{{ documents.certificate.name }}</span>
                    <span class="file-size">{{ formatFileSize(documents.certificate.size) }}</span>
                  </div>
                  <button @click.stop="removeFile('certificate')" class="remove-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.certificate" class="error-message">{{ errors.certificate }}</span>
            </div>

            <!-- Diploma -->
            <div class="document-item">
              <div class="document-header">
                <h4>Diploma</h4>
                <p>Optional: Graduation diploma or certificate</p>
              </div>
              <div
                class="upload-area"
                @click="triggerFileInput('diploma')"
                @drop="handleDrop($event, 'diploma')"
                @dragover.prevent
                @dragenter.prevent
              >
                <input
                  ref="diplomaInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  @change="handleFileChange($event, 'diploma')"
                  style="display: none"
                />
                <div v-if="!documents.diploma" class="upload-placeholder">
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                  </svg>
                  <p>Click to upload or drag and drop</p>
                  <span>PDF, JPG, PNG, DOC, DOCX (Max 10MB)</span>
                </div>
                <div v-else class="uploaded-file">
                  <svg class="file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                    />
                  </svg>
                  <div class="file-info">
                    <span class="file-name">{{ documents.diploma.name }}</span>
                    <span class="file-size">{{ formatFileSize(documents.diploma.size) }}</span>
                  </div>
                  <button @click.stop="removeFile('diploma')" class="remove-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.diploma" class="error-message">{{ errors.diploma }}</span>
            </div>

            <!-- Government Issued ID -->
            <div class="document-item">
              <div class="document-header">
                <h4>Government Issued ID <span class="required">*</span></h4>
                <p>Valid government-issued identification</p>
              </div>
              <div
                class="upload-area"
                @click="triggerFileInput('governmentId')"
                @drop="handleDrop($event, 'governmentId')"
                @dragover.prevent
                @dragenter.prevent
              >
                <input
                  ref="governmentIdInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  @change="handleFileChange($event, 'governmentId')"
                  style="display: none"
                />
                <div v-if="!documents.governmentId" class="upload-placeholder">
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                  </svg>
                  <p>Click to upload or drag and drop</p>
                  <span>PDF, JPG, PNG, DOC, DOCX (Max 10MB)</span>
                </div>
                <div v-else class="uploaded-file">
                  <svg class="file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                    />
                  </svg>
                  <div class="file-info">
                    <span class="file-name">{{ documents.governmentId.name }}</span>
                    <span class="file-size">{{ formatFileSize(documents.governmentId.size) }}</span>
                  </div>
                  <button @click.stop="removeFile('governmentId')" class="remove-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.governmentId" class="error-message">{{
                errors.governmentId
              }}</span>
            </div>

            <!-- Curriculum Vitae -->
            <div class="document-item">
              <div class="document-header">
                <h4>Curriculum Vitae <span class="required">*</span></h4>
                <p>Your complete CV or resume</p>
              </div>
              <div
                class="upload-area"
                @click="triggerFileInput('cv')"
                @drop="handleDrop($event, 'cv')"
                @dragover.prevent
                @dragenter.prevent
              >
                <input
                  ref="cvInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  @change="handleFileChange($event, 'cv')"
                  style="display: none"
                />
                <div v-if="!documents.cv" class="upload-placeholder">
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                  </svg>
                  <p>Click to upload or drag and drop</p>
                  <span>PDF, JPG, PNG, DOC, DOCX (Max 10MB)</span>
                </div>
                <div v-else class="uploaded-file">
                  <svg class="file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                    />
                  </svg>
                  <div class="file-info">
                    <span class="file-name">{{ documents.cv.name }}</span>
                    <span class="file-size">{{ formatFileSize(documents.cv.size) }}</span>
                  </div>
                  <button @click.stop="removeFile('cv')" class="remove-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.cv" class="error-message">{{ errors.cv }}</span>
            </div>

            <!-- Barangay Clearance -->
            <div class="document-item">
              <div class="document-header">
                <h4>Barangay Clearance or NBI Clearance</h4>
                <p>Optional: Required only for face-to-face tutoring</p>
              </div>
              <div
                class="upload-area"
                @click="triggerFileInput('clearance')"
                @drop="handleDrop($event, 'clearance')"
                @dragover.prevent
                @dragenter.prevent
              >
                <input
                  ref="clearanceInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  @change="handleFileChange($event, 'clearance')"
                  style="display: none"
                />
                <div v-if="!documents.clearance" class="upload-placeholder">
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                  </svg>
                  <p>Click to upload or drag and drop</p>
                  <span>PDF, JPG, PNG, DOC, DOCX (Max 10MB)</span>
                </div>
                <div v-else class="uploaded-file">
                  <svg class="file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                    />
                  </svg>
                  <div class="file-info">
                    <span class="file-name">{{ documents.clearance.name }}</span>
                    <span class="file-size">{{ formatFileSize(documents.clearance.size) }}</span>
                  </div>
                  <button @click.stop="removeFile('clearance')" class="remove-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.clearance" class="error-message">{{ errors.clearance }}</span>
            </div>

            <!-- Video Introduction -->
            <div class="document-item full-width">
              <div class="document-header">
                <h4>Video Introduction</h4>
                <p>Optional: 1-3 minute introduction video</p>
              </div>
              <div
                class="upload-area"
                @click="triggerFileInput('video')"
                @drop="handleDrop($event, 'video')"
                @dragover.prevent
                @dragenter.prevent
              >
                <input
                  ref="videoInput"
                  type="file"
                  accept=".mp4,.mov,.avi,.wmv,.webm,.mkv"
                  @change="handleFileChange($event, 'video')"
                  style="display: none"
                />
                <div v-if="!documents.video" class="upload-placeholder">
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                  </svg>
                  <p>Click to upload or drag and drop</p>
                  <span>MP4, MOV, AVI, WMV, WEBM, MKV (Max 50MB)</span>
                </div>
                <div v-else class="uploaded-file">
                  <svg class="file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"
                    />
                  </svg>
                  <div class="file-info">
                    <span class="file-name">{{ documents.video.name }}</span>
                    <span class="file-size">{{ formatFileSize(documents.video.size) }}</span>
                  </div>
                  <button @click.stop="removeFile('video')" class="remove-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.video" class="error-message">{{ errors.video }}</span>
            </div>
          </div>
        </div>

        <!-- Terms and Agreements Section -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M9,4V6H14V4H16V6H17A1,1 0 0,1 18,7V19A1,1 0 0,1 17,20H7A1,1 0 0,1 6,19V7A1,1 0 0,1 7,6H8V4H9M7,8V18H17V8H7Z"
                />
              </svg>
            </div>
            <div class="section-title">
              <h3>Terms and Agreements</h3>
              <p>Please read and agree to all terms and conditions</p>
            </div>
          </div>

          <!-- Tutor Service Agreement -->
          <div class="agreement-section">
            <div class="agreement-header">
              <svg class="agreement-icon" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                />
              </svg>
              <h4>Tutor Service Agreement Contract</h4>
            </div>
          

            <div class="download-section">
              <button @click="downloadContract" class="download-btn">
                <svg class="download-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                </svg>
                Download ElevatED Tutor Contract (PDF)
              </button>
              <span class="file-info-text">File size: ~450KB | Last updated: October 2025</span>
            </div>

            <div class="contract-upload">
              <h5>Upload Signed Contract <span class="required">*</span></h5>
              <p>
                Please download the official ElevatED Tutor Service Agreement, read it carefully, sign
              it, and upload the signed document below.
              </p>
              <div
                class="upload-area contract-upload-area"
                @click="triggerFileInput('signedContract')"
                @drop="handleDrop($event, 'signedContract')"
                @dragover.prevent
                @dragenter.prevent
              >
                <input
                  ref="signedContractInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="handleFileChange($event, 'signedContract')"
                  style="display: none"
                />
                <div v-if="!documents.signedContract" class="upload-placeholder">
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                  </svg>
                  <p>Click to upload signed contract</p>
                  <span>PDF, JPG, PNG (Max 10MB)</span>
                </div>
                <div v-else class="uploaded-file">
                  <svg class="file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                    />
                  </svg>
                  <div class="file-info">
                    <span class="file-name">{{ documents.signedContract.name }}</span>
                    <span class="file-size">{{
                      formatFileSize(documents.signedContract.size)
                    }}</span>
                  </div>
                  <button @click.stop="removeFile('signedContract')" class="remove-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.signedContract" class="error-message">{{
                errors.signedContract
              }}</span>
            </div>
          </div>

          <!-- Agreement Checkboxes -->
          <div class="checkboxes-section">
            <div class="checkbox-item">
              <input
                id="studentSafety"
                v-model="agreements.studentSafety"
                type="checkbox"
                :class="{ error: errors.studentSafety }"
              />
              <label for="studentSafety">
                <span class="required">*</span> I understand and agree to follow all student safety
                protocols, including maintaining appropriate boundaries, ensuring a safe learning
                environment, and reporting any safety concerns immediately.
              </label>
              <span v-if="errors.studentSafety" class="error-message">{{
                errors.studentSafety
              }}</span>
            </div>

            <div class="checkbox-item">
              <input
                id="liabilityTerms"
                v-model="agreements.liabilityTerms"
                type="checkbox"
                :class="{ error: errors.liabilityTerms }"
              />
              <label for="liabilityTerms">
                <span class="required">*</span> I accept all liability terms and understand my
                responsibilities as a tutor, including professional conduct, confidentiality of
                student information, and adherence to platform policies.
              </label>
              <span v-if="errors.liabilityTerms" class="error-message">{{
                errors.liabilityTerms
              }}</span>
            </div>

            <div class="checkbox-item">
              <input
                id="conductStandards"
                v-model="agreements.conductStandards"
                type="checkbox"
                :class="{ error: errors.conductStandards }"
              />
              <label for="conductStandards">
                <span class="required">*</span> I agree to maintain the highest platform conduct
                standards, including professional communication, punctuality, quality instruction,
                and respectful interaction with all platform users.
              </label>
              <span v-if="errors.conductStandards" class="error-message">{{
                errors.conductStandards
              }}</span>
            </div>

            <div class="checkbox-item">
              <input
                id="termsAndConditions"
                v-model="agreements.termsAndConditions"
                type="checkbox"
                :class="{ error: errors.termsAndConditions }"
              />
              <label for="termsAndConditions">
                <span class="required">*</span> I have read and agree to all terms and conditions,
                and I understand that I may be held legally accountable if I do not abide by the
                above agreements and platform policies.
              </label>
              <span v-if="errors.termsAndConditions" class="error-message">{{
                errors.termsAndConditions
              }}</span>
            </div>
          </div>

          <!-- Legal Notice -->
          <div class="legal-notice">
            <div class="notice-header">
              <svg class="warning-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z" />
              </svg>
              <h4>Important Legal Notice</h4>
            </div>
            <p>
              This Tutor Service Agreement shall become effective and legally binding only upon the
              successful approval and acceptance of your tutor application by ElevatED. In the event
              of rejection or non-acceptance, this agreement shall be considered null and void,
              imposing no legal obligations on either party.
            </p>
            <div class="checkbox-item">
              <input
                id="legalNotice"
                v-model="agreements.legalNotice"
                type="checkbox"
                :class="{ error: errors.legalNotice }"
              />
              <label for="legalNotice">
                <span class="required">*</span> I acknowledge and agree to the terms stated in this
                Important Legal Notice regarding the binding nature of this agreement.
              </label>
              <span v-if="errors.legalNotice" class="error-message">{{ errors.legalNotice }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="global-error">
        {{ errorMessage }}
      </div>

      <!-- Button Container -->
      <div class="button-container">
        <button @click="goBack" class="back-btn">
          <svg class="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41,16.58L10.83,12L15.41,7.42L14,6L8,12L14,18L15.41,16.58Z" />
          </svg>
          Back
        </button>

        <button
          @click="handleSubmit"
          :disabled="isLoading || !isFormValid"
          class="complete-btn"
          :class="{ disabled: !isFormValid }"
        >
          <span v-if="isLoading">Creating Account...</span>
          <span v-else>Complete Registration</span>
          <svg class="check-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  auth,
  db,
  storage,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  doc,
  setDoc,
  serverTimestamp,
  storageRef,
  uploadBytes,
  getDownloadURL,
} from "@/firebase";
import { useTutorRegistrationStorage, type TutorDocumentsData, type TutorAgreementsData } from "@/composables/useRegistrationStorage";
import { logDebug } from '@/utils/secureLogger';

// Props to receive data from previous step
interface Props {
  tutorData?: {
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    email: string;
    dateOfBirth: string;
    password: string;
    confirmPassword: string;
    subjects: string;
    experienceYears: string;
    educationLevel: string;
    fieldOfStudy: string;
    institution: string;
    graduationYear: string;
  };
  initialDocuments?: Record<string, File | null>;
  initialAgreements?: Record<string, boolean>;
  requiredDocs?: string[] | undefined;
  isResubmission?: boolean;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  "registration-complete": [
    data: {
      tutorData: any;
      documents: Record<string, File>;
      agreements: Record<string, boolean>;
    }
  ];
  "go-back": [];
  "data-changed": [
    data: {
      documents: Record<string, File | null>;
      agreements: Record<string, boolean>;
    }
  ];
}>();

const router = useRouter();

// Initialize registration storage
const { saveTutorDocuments, loadTutorDocuments, saveTutorAgreements, loadTutorAgreements } = useTutorRegistrationStorage();

// Document storage
const documents = reactive<Record<string, File | null>>({
  transcript: null,
  certificate: null,
  diploma: null,
  governmentId: null,
  cv: null,
  clearance: null,
  video: null,
  signedContract: null,
});

// Agreements
const agreements = reactive({
  studentSafety: false,
  liabilityTerms: false,
  conductStandards: false,
  termsAndConditions: false,
  legalNotice: false,
});

// Load saved data on mount
onMounted(() => {
  // First try to load from props (when navigating back)
  if (props.initialAgreements) {
    logDebug('Restoring tutor agreements from props');
    Object.assign(agreements, props.initialAgreements);
  } else {
    // If no props, try to load from sessionStorage
    const savedAgreements = loadTutorAgreements();
    if (savedAgreements) {
      logDebug('Restoring tutor agreements from sessionStorage');
      Object.assign(agreements, savedAgreements);
    }
  }
  
  // Note: File objects cannot be restored from sessionStorage
  // But we can show a message or indication if files were previously uploaded
  
  checkFormValidity();
});

// Auto-save agreements to sessionStorage
watch(agreements, () => {
  const agreementsToSave: TutorAgreementsData = {
    studentSafety: agreements.studentSafety,
    liabilityTerms: agreements.liabilityTerms,
    conductStandards: agreements.conductStandards,
    termsAndConditions: agreements.termsAndConditions,
    legalNotice: agreements.legalNotice,
  };
  saveTutorAgreements(agreementsToSave);
}, { deep: true });

// Auto-save document metadata to sessionStorage (not actual files)
watch(documents, () => {
  const documentsMetadata: TutorDocumentsData = {
    validIDFileName: documents.governmentId?.name,
    proofOfEducationFileName: documents.transcript?.name,
    certificationsFileName: documents.certificate?.name,
  };
  saveTutorDocuments(documentsMetadata);
}, { deep: true });

// State
const isLoading = ref(false);
const errorMessage = ref("");
const isFormValid = ref(false);

// Validation errors
const errors = reactive({
  transcript: "",
  certificate: "",
  diploma: "",
  governmentId: "",
  cv: "",
  clearance: "",
  video: "",
  signedContract: "",
  studentSafety: "",
  liabilityTerms: "",
  conductStandards: "",
  termsAndConditions: "",
  legalNotice: "",
});

// File input refs
const transcriptInput = ref<HTMLInputElement>();
const certificateInput = ref<HTMLInputElement>();
const diplomaInput = ref<HTMLInputElement>();
const governmentIdInput = ref<HTMLInputElement>();
const cvInput = ref<HTMLInputElement>();
const clearanceInput = ref<HTMLInputElement>();
const videoInput = ref<HTMLInputElement>();
const signedContractInput = ref<HTMLInputElement>();

// Watch for initial data changes and populate forms
watch(
  () => props.initialDocuments,
  (newDocs) => {
    if (newDocs) {
      Object.keys(newDocs).forEach((key) => {
        if (newDocs[key]) {
          documents[key] = newDocs[key];
        }
      });
    }
  },
  { immediate: true }
);

watch(
  () => props.initialAgreements,
  (newAgreements) => {
    if (newAgreements) {
      Object.keys(newAgreements).forEach((key) => {
        if (key in agreements) {
          agreements[key as keyof typeof agreements] = newAgreements[key];
        }
      });
    }
  },
  { immediate: true }
);

// Watch for changes in documents and agreements to emit to parent
watch(
  [documents, agreements],
  () => {
    emit("data-changed", {
      documents: { ...documents },
      agreements: { ...agreements },
    });
  },
  { deep: true }
);

// Check overall form validity for button state
const checkFormValidity = () => {
  // Check required documents
  const hasTranscript = documents.transcript !== null;
  const hasGovernmentId = documents.governmentId !== null;
  const hasCv = documents.cv !== null;
  const hasSignedContract = documents.signedContract !== null;

  // Check required agreements
  const hasStudentSafety = agreements.studentSafety;
  const hasLiabilityTerms = agreements.liabilityTerms;
  const hasConductStandards = agreements.conductStandards;
  const hasTermsAndConditions = agreements.termsAndConditions;
  const hasLegalNotice = agreements.legalNotice;

  isFormValid.value = Boolean(
    hasTranscript &&
      hasGovernmentId &&
      hasCv &&
      hasSignedContract &&
      hasStudentSafety &&
      hasLiabilityTerms &&
      hasConductStandards &&
      hasTermsAndConditions &&
      hasLegalNotice
  );
};

// Watch for changes in documents and agreements to update form validity
watch(
  [documents, agreements],
  () => {
    checkFormValidity();
  },
  { deep: true }
);

// File handling functions
function triggerFileInput(documentType: string) {
  const inputMap: Record<string, any> = {
    transcript: transcriptInput.value,
    certificate: certificateInput.value,
    diploma: diplomaInput.value,
    governmentId: governmentIdInput.value,
    cv: cvInput.value,
    clearance: clearanceInput.value,
    video: videoInput.value,
    signedContract: signedContractInput.value,
  };

  inputMap[documentType]?.click();
}

function handleFileChange(event: Event, documentType: string) {
  const input = event.target as HTMLInputElement;
  if (!input.files || !input.files[0]) return;

  const file = input.files[0];

  // Validate file size
  const maxSize = documentType === "video" ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for video, 10MB for others
  if (file.size > maxSize) {
    errors[documentType as keyof typeof errors] = `File size must be less than ${
      documentType === "video" ? "50MB" : "10MB"
    }`;
    return;
  }

  documents[documentType] = file;
  errors[documentType as keyof typeof errors] = "";
}

function handleDrop(event: DragEvent, documentType: string) {
  event.preventDefault();
  if (!event.dataTransfer?.files || !event.dataTransfer.files[0]) return;

  const file = event.dataTransfer.files[0];

  // Define allowed file types for each document
  const allowedTypes: Record<string, string[]> = {
    transcript: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
    certificate: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
    diploma: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
    governmentId: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
    cv: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
    clearance: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
    video: ['.mp4', '.mov', '.avi', '.wmv', '.webm', '.mkv'],
    signedContract: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx']
  };

  // Get file extension
  const fileName = file.name.toLowerCase();
  const fileExtension = '.' + fileName.split('.').pop();

  // Validate file type
  const allowedExtensions = allowedTypes[documentType] || [];
  if (!allowedExtensions.includes(fileExtension)) {
    const allowedTypesText = documentType === 'video' 
      ? 'MP4, MOV, AVI, WMV, WEBM, MKV' 
      : 'PDF, JPG, PNG, DOC, DOCX';
    errors[documentType as keyof typeof errors] = `Invalid file type. Please upload: ${allowedTypesText}`;
    return;
  }

  // Validate file size
  const maxSize = documentType === "video" ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
  if (file.size > maxSize) {
    errors[documentType as keyof typeof errors] = `File size must be less than ${
      documentType === "video" ? "50MB" : "10MB"
    }`;
    return;
  }

  documents[documentType] = file;
  errors[documentType as keyof typeof errors] = "";
}

function removeFile(documentType: string) {
  documents[documentType] = null;
  errors[documentType as keyof typeof errors] = "";
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function downloadContract() {
  // Download the tutor contract PDF from the public folder
  const link = document.createElement('a');
  link.href = '/tutor-contract.pdf';
  link.download = 'ElevatED-Tutor-Contract.pdf';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  console.log("Downloading tutor contract...");
}

// Validation
function validateForm(): boolean {
  let isValid = true;

  // Reset errors
  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = "";
  });

  // Required documents
  if (!documents.transcript) {
    errors.transcript = "Official transcript is required";
    isValid = false;
  }

  if (!documents.governmentId) {
    errors.governmentId = "Government issued ID is required";
    isValid = false;
  }

  if (!documents.cv) {
    errors.cv = "Curriculum Vitae is required";
    isValid = false;
  }

  if (!documents.signedContract) {
    errors.signedContract = "Signed contract is required";
    isValid = false;
  }

  // Required agreements
  if (!agreements.studentSafety) {
    errors.studentSafety = "You must agree to student safety protocols";
    isValid = false;
  }

  if (!agreements.liabilityTerms) {
    errors.liabilityTerms = "You must accept liability terms";
    isValid = false;
  }

  if (!agreements.conductStandards) {
    errors.conductStandards = "You must agree to conduct standards";
    isValid = false;
  }

  if (!agreements.termsAndConditions) {
    errors.termsAndConditions = "You must agree to terms and conditions";
    isValid = false;
  }

  if (!agreements.legalNotice) {
    errors.legalNotice = "You must acknowledge the legal notice";
    isValid = false;
  }

  return isValid;
}

function goBack() {
  emit("go-back");
}

// Upload a single document to Firebase Storage
async function uploadDocument(
  file: File,
  docType: string,
  userId: string
): Promise<{ name: string; url: string; type: string; size: number }> {
  try {
    // Create a unique filename
    const timestamp = Date.now();
    const fileExtension = file.name.split(".").pop();
    const fileName = `${docType}_${timestamp}.${fileExtension}`;

    // Create storage reference
    const fileRef = storageRef(storage, `tutorDocuments/${userId}/${fileName}`);

    // Upload file
    console.log(`Uploading ${docType}:`, file.name);
    const snapshot = await uploadBytes(fileRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    // 🔥 Create audit log for file upload (resubmission or initial)
    if (props.isResubmission) {
      try {
        const { createAuditLog } = await import('@/services/auditLogService')
        const currentUser = auth.currentUser;
        
        // Document name mapping for friendly display
        const documentNameMapping: Record<string, string> = {
          transcript: "Official Transcript of Grades",
          certificate: "Certificate of Grade",
          diploma: "Diploma",
          governmentId: "Government Issued ID",
          cv: "Curriculum Vitae",
          clearance: "Clearance",
          video: "Video Introduction",
          signedContract: "Signed Contract"
        };
        
        const documentName = documentNameMapping[docType] || docType;
        
        await createAuditLog({
          action: 'document_upload',
          type: 'document_access',
          details: `Tutor resubmitted document: ${documentName} (${file.name}) upon admin's request`,
          performedById: currentUser?.uid || userId,
          performedByName: currentUser?.displayName || currentUser?.email || 'Tutor',
          performedByEmail: currentUser?.email || '',
          affectedUserId: userId,
          affectedUserName: currentUser?.displayName || currentUser?.email || 'Tutor',
          affectedUserEmail: currentUser?.email || '',
          metadata: {
            documentId: docType,
            documentType: documentName,
            accessType: 'upload',
            fileName: file.name,
            fileSize: file.size
          }
        })
        console.log('Audit log created for document resubmission', { docType, fileName: file.name })
      } catch (auditError) {
        console.error('Failed to create audit log for document resubmission', auditError)
        // Continue anyway - audit log is not critical for file upload
      }
    }

    return {
      name: file.name,
      url: downloadURL,
      type: file.type,
      size: file.size,
    };
  } catch (error) {
    console.error(`Error uploading ${docType}:`, error);
    throw error;
  }
}

// Upload all documents to Firebase Storage
async function uploadDocuments(userId: string): Promise<Record<string, any>> {
  const uploadedDocs: Record<string, any> = {};

  for (const [docType, file] of Object.entries(documents)) {
    if (file && file instanceof File) {
      try {
        uploadedDocs[docType] = await uploadDocument(file, docType, userId);
        console.log(`Successfully uploaded ${docType}`);
      } catch (error) {
        console.error(`Failed to upload ${docType}:`, error);
        throw new Error(`Failed to upload ${docType}. Please try again.`);
      }
    }
  }

  return uploadedDocs;
}

// Main submission function - collect data and proceed to email verification
async function handleSubmit() {
  if (!validateForm()) {
    errorMessage.value = "Please complete all required fields and agreements";
    return;
  }

  if (!props.tutorData) {
    errorMessage.value = "Missing tutor information. Please go back to step 1.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";

  try {
    console.log("TutorDocumentsAndTerms: Step 2 completed - proceeding to email verification...");

    // Emit all collected data to parent component for email verification step
    emit("registration-complete", {
      tutorData: props.tutorData,
      documents: documents as Record<string, File>,
      agreements: agreements,
    });
  } catch (error: any) {
    console.error("TutorDocumentsAndTerms: Step 2 error:", error);
    errorMessage.value = "An error occurred. Please try again.";
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped>
.registration-container {
  min-height: 100vh;
  background: url("/tutor-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
}

.progress-header {
  padding: 40px 20px 0px;
  padding-top: 160px; /* Increased top padding to avoid nav bar overlap */
  text-align: center;
  position: relative;
  margin-bottom: 0px;
}

.header-content {
  max-width: 800px;
  margin: 0 auto;
}

.title-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5em;
  margin-right: 7em;
}

.back-button {
  width: 60px;
  height: 60px;
  background: white;
  border: 3px solid #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  color: #000000;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.back-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  color: #000000;
}

.back-button svg {
  width: 30px;
  height: 30px;
}

.title-content {
  text-align: center;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #000000;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.subtitle {
  font-size: 1.1rem;
  color: #333333;
  margin: 0 0 40px 0;
  font-style: italic;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.step-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex-wrap: wrap;
  margin: 0px 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.step-number {
  width: 50px;
  height: 50px;
  border: 3px solid #000000;
  border-radius: 50%;
  background: #888888;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  transition: all 0.3s ease;
  padding: 4px;
}

.step:first-child .step-number {
  background: #a2caff !important;
  color: #000000 !important;
  border-color: #000000 !important;
}

.step:first-child .step-number:hover {
  background: #a2caff !important;
  color: #000000 !important;
  border-color: #000000 !important;
}

.step.active .step-number {
  background: #a2caff;
  border-color: #000000;
  color: #000000;
}

.step.completed .step-number {
  background: #a2caff;
  border-color: #000000;
  color: #000000;
}

.step-label {
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  text-align: center;
  max-width: 120px;
  line-height: 1.3;
}

.step.active .step-label {
  color: #000000;
  font-weight: 700;
}

.step.completed .step-label {
  color: #000000;
  font-weight: 600;
}

.step-line {
  width: 60px;
  height: 4px;
  background: #000000;
  margin: 0 8px;
  border-radius: 2px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 40px;
}

.form-card {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  width: 100%;
  max-width: 1000px;
  margin: 20px auto 20px auto; /* Proper spacing to prevent cutting */
}

.form-section {
  background: white;
  border: 3px solid #090909;
  overflow: hidden;
  border-radius: 16px;
  padding: 36px;
  margin-bottom: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  padding: 18px;
  padding-bottom: 18px;
  border-bottom: none;
  background-color: transparent;
  border-radius: 0;
  margin: -36px -36px 16px -36px;
}

.section-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.section-icon svg {
  width: 28px;
  height: 28px;
}

.section-title h3 {
  margin: 0;
  color: #1e40af;
  font-size: 20px;
  font-weight: 600;
}

.section-title p {
  margin: 4px 0 0 0;
  color: #3b82f6;
  font-size: 14px;
  font-style: normal;
}

.documents-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.document-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
}

.document-item.full-width {
  grid-column: 1 / -1;
}

.document-header {
  margin-bottom: 16px;
}

.document-header h4 {
  margin: 0 0 4px 0;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}

.required {
  color: #dc2626;
  font-weight: 700;
}

.document-header p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.upload-area:hover {
  border-color: #1e40af;
  background: #eff6ff;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-icon {
  width: 32px;
  height: 32px;
  color: #6b7280;
}

.upload-placeholder p {
  margin: 0;
  color: #374151;
  font-weight: 500;
}

.upload-placeholder span {
  color: #6b7280;
  font-size: 12px;
}

.uploaded-file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 6px;
}

.file-icon {
  width: 24px;
  height: 24px;
  color: #1e40af;
  flex-shrink: 0;
}

.file-info {
  flex-grow: 1;
  text-align: left;
  min-width: 0;
  max-width: 200px;
  overflow: hidden;
}

.file-name {
  display: block;
  color: #1f2937;
  font-weight: 500;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  color: #6b7280;
  font-size: 12px;
}

.remove-btn {
  width: 24px;
  height: 24px;
  background: #ef4444;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.remove-btn:hover {
  background: #dc2626;
}

.remove-btn svg {
  width: 16px;
  height: 16px;
}

.agreement-section {
  background: #eff6ff;
  border: 1px solid #3b82f6;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
}

.agreement-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.agreement-icon {
  width: 24px;
  height: 24px;
  color: #1e40af;
}

.agreement-header h4 {
  margin: 0;
  color: #1e40af;
  font-size: 18px;
  font-weight: 600;
}

.agreement-description {
  color: #1e40af;
  font-size: 12px;
  margin-bottom: 8px;
  line-height: 1.3;
}

.download-section {
  margin-bottom: 12px;
}

.download-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-bottom: 8px;
}

.download-btn:hover {
  background: #1d4ed8;
}

.download-icon {
  width: 20px;
  height: 20px;
}

.file-info-text {
  color: #6b7280;
  font-size: 12px;
}

.contract-upload {
  margin-top: 20px;
}

.contract-upload h5 {
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}

.contract-upload p {
  margin: 0 0 16px 0;
  color: #6b7280;
  font-size: 14px;
}

.contract-upload-area {
  border-color: #1e40af;
}

.checkboxes-section {
  margin-top: 12px;
}

.checkbox-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 6px;
}

.checkbox-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  flex-shrink: 0;
}

.checkbox-item input[type="checkbox"].error {
  outline: 2px solid #dc2626;
}

.checkbox-item label {
  color: #374151;
  font-size: 12px;
  line-height: 1.3;
  cursor: pointer;
}

.legal-notice {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
}

.notice-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.warning-icon {
  width: 24px;
  height: 24px;
  color: #f59e0b;
}

.notice-header h4 {
  margin: 0;
  color: #92400e;
  font-size: 16px;
  font-weight: 600;
}

.legal-notice p {
  color: #92400e;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 12px;
}

.error-message {
  color: #dc2626;
  font-size: 14px;
  margin-top: 5px;
  display: block;
}

.global-error {
  background: #fdf2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  text-align: center;
}

.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 24px;
  gap: 16px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: auto;
  min-width: 180px;
  padding: 18px 32px;
  background: white;
  color: #4f46e5;
  border: 3px solid #000000;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: none;
  box-shadow: 4px 4px 0px #000000;
}

.back-btn:hover {
  background: #4f46e5;
  color: white;
  transform: translateY(-2px);
  box-shadow: 6px 6px 0px #000000;
}

.complete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: auto;
  min-width: 250px;
  padding: 18px 32px;
  background: #4f46e5;
  color: white;
  border: 3px solid #000000;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: none;
  box-shadow: 4px 4px 0px #000000;
}

.complete-btn:hover:not(:disabled) {
  background: #3730a3;
  transform: translateY(-2px);
  box-shadow: 6px 6px 0px #000000;
}

.complete-btn:disabled,
.complete-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #9ca3af;
  border-color: #6b7280;
  box-shadow: 2px 2px 0px #6b7280;
  transform: none;
}

.arrow-icon,
.check-icon {
  width: 24px;
  height: 24px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .documents-grid {
    grid-template-columns: 1fr;
  }

  .progress-header {
    padding: 30px 16px 20px;
    padding-top: 100px;
  }

  .title-section {
    gap: 16px;
    margin-bottom: 24px;
  }

  .back-button {
    width: 36px;
    height: 36px;
  }

  .back-button svg {
    width: 18px;
    height: 18px;
  }

  .main-title {
    font-size: 2rem;
    margin-bottom: 12px;
  }

  .subtitle {
    font-size: 1rem;
    margin-bottom: 24px;
  }

  .step-number {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .step-line {
    width: 50px;
  }

  .content-wrapper {
    padding: 20px 16px 40px;
  }

  .form-card {
    padding: 0;
    margin: 0 auto 15px auto;
  }

  .form-section {
    padding: 36px;
    margin-bottom: 28px;
  }

  .section-header {
    gap: 15px;
    margin: -36px -36px 16px -36px;
  }

  .section-icon {
    width: 48px;
    height: 48px;
  }

  .section-icon svg {
    width: 24px;
    height: 24px;
  }

  .section-title h3 {
    font-size: 20px;
  }

  .section-title p {
    font-size: 14px;
  }

  .button-container {
    justify-content: center;
    flex-direction: column-reverse;
    margin-top: 20px;
    margin-bottom: 20px;
    gap: 16px;
  }

  .back-btn {
    min-width: 180px;
    padding: 16px 28px;
    font-size: 16px;
    font-weight: 700;
  }

  .complete-btn {
    min-width: 220px;
    padding: 16px 28px;
    font-size: 16px;
    font-weight: 700;
  }
}

@media (max-width: 480px) {
  .progress-header {
    padding: 20px 12px 16px;
    padding-top: 80px;
  }

  .title-section {
    gap: 12px;
    margin: 0 3em 0 1em !important;
    margin-bottom: 20px;
  }

  .back-button {
    width: 32px;
    height: 32px;
  }

  .back-button svg {
    width: 16px;
    height: 16px;
  }

  .main-title {
    font-size: 1.75rem;
    margin-bottom: 8px;
  }

  .subtitle {
    font-size: 0.9rem;
    margin-bottom: 20px;
  }

  .step-progress {
    gap: 12px;
  }

  .step-number {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .step-line {
    width: 40px;
  }

  .content-wrapper {
    padding: 16px 12px 32px;
  }

  .form-card {
    padding: 0;
    margin: 0 auto 10px auto;
  }

  .form-section {
    padding: 24px;
    margin-bottom: 24px;
  }

  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    margin: -24px -24px 24px -24px;
  }

  .section-icon {
    width: 40px;
    height: 40px;
    margin: 0 auto;
  }

  .section-icon svg {
    width: 20px;
    height: 20px;
  }

  .section-title h3 {
    font-size: 18px;
  }

  .section-title p {
    font-size: 13px;
  }

  .documents-grid {
    grid-template-columns: 1fr;
  }

  .file-name {
    max-width: 180px;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .button-container {
    justify-content: center;
    flex-direction: column-reverse;
    margin-top: 15px;
    margin-bottom: 15px;
    gap: 12px;
  }

  .back-btn {
    min-width: 160px;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 700;
  }

  .complete-btn {
    min-width: 200px;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 700;
  }
}
</style>
